/**
 * Stage 3a: Content Brief — context-based version using StagePanel.
 *
 * Generates the article structure (outline, SEO, FAQ) from all research.
 * LLM stage. Brief editing goes through the document editor.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const targeting = document?.targeting;
  const research = document?.content_research;
  const hasKeywords = Boolean(targeting?.keywords?.primary);
  const hasSerp = Boolean(targeting?.serp?.overview);
  const hasAnalysis = Boolean(targeting?.competitors?.analysis);
  const hasLinks = (research?.internal_link_candidates || []).length > 0;

  return (
    <div className="text-xs text-gray-600 space-y-1 bg-gray-50 rounded border p-2">
      <div>
        Primary keyword: {targeting?.keywords?.primary?.keyword || '—'}
        {hasKeywords && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        SERP analysis: {hasSerp ? targeting?.serp?.overview?.decision : '—'}
        {hasSerp && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        Competitor analysis: {hasAnalysis ? 'Complete' : '—'}
        {hasAnalysis && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
      <div>
        Internal links: {hasLinks ? `${research!.internal_link_candidates!.length} candidates` : '—'}
        {hasLinks && <span className="text-green-600 ml-1">&#10003;</span>}
      </div>
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const article = doc.article;
  if (!article?.sections || article.sections.length === 0) {
    return <div className="text-sm text-gray-500">No brief generated yet.</div>;
  }
  return (
    <div className="space-y-3">
      {article.summary && (
        <div>
          <div className="text-xs font-medium text-gray-700">Summary</div>
          <div className="text-xs text-gray-600">{article.summary}</div>
        </div>
      )}
      {article.seo && (
        <div>
          <div className="text-xs font-medium text-gray-700">SEO</div>
          <div className="text-xs text-gray-600">
            {article.seo.meta_title} | /{article.seo.slug}
          </div>
        </div>
      )}
      <div>
        <div className="text-xs font-medium text-gray-700 mb-1">
          Outline ({article.sections.length} sections)
        </div>
        <div className="space-y-1">
          {article.sections.map((s) => (
            <div key={s.id} className="text-xs border rounded px-2 py-1 border-gray-200">
              <span className="text-gray-400">{s.heading_level}</span>{' '}
              <span className="font-medium text-gray-800">{s.heading || '(Intro)'}</span>
              {s.key_points.length > 0 && (
                <span className="text-gray-400 ml-2">
                  {s.key_points.length} key point{s.key_points.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      {article.faq && article.faq.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700">FAQ ({article.faq.length})</div>
          {article.faq.map((f, i) => (
            <div key={i} className="text-xs text-gray-600">Q: {f.question}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Stage3aBriefV2() {
  const { document } = useArticleContext();
  const hasResearch = Boolean(
    document?.targeting?.keywords?.primary &&
    document?.targeting?.serp?.overview &&
    document?.targeting?.competitors?.analysis
  );

  return (
    <StagePanel
      stageId="stage_3a_brief"
      isLlmStage
      title="Stage 3a: Content Brief"
      description="Generates the article outline, SEO metadata, and FAQ from all research data."
      runDisabled={!hasResearch}
      runDisabledReason={!hasResearch ? 'Requires keyword selection, SERP analysis, and competitor analysis' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
